import { useRef, useState, useEffect } from 'react';
import s from './ReferencesBlock.module.css';
import { useSelector } from 'react-redux';
import {
    selectCurrentProject,
    selectIsSearchingReferences,
    selectReferences,
} from '../../../../store/selectors/projectSelectors';
import { useAppDispatch } from '../../../../store/store';
import { searchReference } from '../../../../shared/api/textProjectApi';
import { quillRef } from '../../../../store/ref/quillRef';

import Button from '../../../../shared/ui/button/Button';
import ReferencesModal from './ReferencesModal';
import { toast } from 'react-toastify';
import { setReferences } from '../../../../store/slices/projectSlice';
import { selectIsFreeSubscription } from '../../../../store/selectors/subscriptionSelector';
import { selectIsBasicSubscription } from '../../../../store/selectors/subscriptionSelector';
import { setPricingModal } from '../../../../store/slices/pageSlice';
import IconLock from '../../../../shared/ui/icons/IconLock';

const ReferencesBlock = () => {
    const references = useSelector(selectReferences);
    const isSearching = useSelector(selectIsSearchingReferences);
    const currentProject = useSelector(selectCurrentProject);
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);
    const dispatch = useAppDispatch();
    const isFreeSubscription = useSelector(selectIsFreeSubscription);
    const isBasicSubscription = useSelector(selectIsBasicSubscription);

    const isRestrictPrompt = isFreeSubscription || isBasicSubscription;

    useEffect(() => {
        if (references && references.length > 0) {
            setIsModalOpen(true);
        }
    }, [references]);

    const handleSearch = async () => {
        if (!currentProject?.id) return;
        if (isRestrictPrompt) {
            dispatch(setPricingModal(true));
            return;
        }
        await dispatch(searchReference({ projectId: currentProject.id }));
    };

    const handleCopyReference = (id: number, url: string) => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        navigator.clipboard.writeText(url);
        setCopiedId(id);
        toast.success('Copied to clipboard');

        debounceRef.current = setTimeout(() => {
            setCopiedId(null);
        }, 1000);
    };

    // const toggleActive = (id: number) => {
    //     setActiveId((prev) => (prev === id ? null : id));
    // };

    const handlePasteReference = (id: number, url: string) => {
        if (!url || !quillRef?.current) return;

        const quill = quillRef.current.getEditor();
        const text = url.replace(/\\n/g, '\n');

        quill.focus();
        let range = quill.getSelection(true);
        if (!range) range = { index: quill.getLength(), length: 0 };

        if (range.length > 0) {
            quill.deleteText(range.index, range.length, 'user');
        }

        const currentFormats = quill.getFormat(range.index, 0);
        quill.insertText(range.index, text, currentFormats, 'user');

        quill.setSelection(range.index, text.length, 'silent');
        toast.success('Pasted reference');
    };

    return (
        <>
            <div className={s.container}>
                <h3 className={s.title}>Search references</h3>
                <p className={s.subtitle}>Let’s find some references to your content on the web!</p>

                <Button
                    onClick={handleSearch}
                    state={isSearching ? 'loading' : 'enabled'}
                    text="Start searching"
                    style={{ background: '#FFDD35', marginTop: '20px', fontWeight: '600' }}
                    icon={isRestrictPrompt ? <IconLock color="black" /> : undefined}
                />
            </div>

            <ReferencesModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    dispatch(setReferences([]));
                }}
                references={references || []}
                onSearchAgain={handleSearch}
                onCopyReference={handleCopyReference}
                onPasteReference={handlePasteReference}
            />
        </>
    );
};

export default ReferencesBlock;
